/* import __COLOCATED_TEMPLATE__ from './filter-predicates-editor.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import Component from '@glimmer/component';
import type ImportSource from 'embercom/models/import-service/import-source';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type IntlService from 'ember-intl/services/intl';
import { tracked } from '@glimmer/tracking';

interface Args {
  predicates: $TSFixMe;
  importSource?: ImportSource;
  onUpdatePredicates?: (predicates: $TSFixMe) => void;
}

interface SalesforceMetadataField {
  name: string;
  type: string;
  label: string;
  filterable: boolean;
  picklistValues?: any[];
}

// Salesforce does not allow COUNT when these fields are used in the WHERE clause
const RESTRICTED_FILTER_TYPES = ['id', 'reference'];
const RESTRICTED_FILTER_FIELDS = ['VersionNumber'];
const LIST_FIELD_TYPES = ['picklist', 'multipicklist'];

// eslint-disable-next-line @intercom/intercom/no-bare-strings
const STANDARD_FILTERS_FIELDS = ['ArticleType', 'CreatedDate', 'IsDeleted', 'Language'];

export default class FilterPredicatesEditor extends Component<Args> {
  @service declare store: Store;
  @service declare intl: IntlService;

  @tracked attributes: any[];

  constructor(owner: any, args: Args) {
    super(owner, args);
    this.attributes = this.args.importSource?.metadata.firstObject.fields
      .filter((field: SalesforceMetadataField) => this.isSupportedField(field))
      .map((field: SalesforceMetadataField) => {
        return this.buildAttribute(field);
      });
  }

  get attributesGroupList() {
    let standardAttributes = this.attributes
      ?.filter((attribute: any) => STANDARD_FILTERS_FIELDS.includes(attribute.identifier))
      .sort((a: any, b: any) => a.name.localeCompare(b.name));

    let customAttributes = this.attributes
      ?.filter((attribute: any) => !STANDARD_FILTERS_FIELDS.includes(attribute.identifier))
      .sort((a: any, b: any) => a.name.localeCompare(b.name));

    return [
      {
        heading: this.intl.t(
          'standalone.salesforce.setup.article-sync.steps.filter.standard-fields',
        ),
        attributes: standardAttributes,
      },
      {
        heading: this.intl.t('standalone.salesforce.setup.article-sync.steps.filter.custom-fields'),
        attributes: customAttributes,
      },
    ];
  }

  get supportedPredicateAttributes() {
    return this.attributes.map((attribute: any) => attribute.identifier);
  }

  private buildAttribute(field: SalesforceMetadataField) {
    let options = this.buildOptions(field);
    let attribute = this.store.createRecord('attribute', {
      identifier: `salesforce.knowledge.${field.name}`,
      name: field.label,
      type: this.getAttributeType(field.type),
      icon: this.attributeIcon(field.type),
    });

    attribute.options.pushObjects(options);

    return attribute;
  }

  private isSupportedField(field: SalesforceMetadataField) {
    return (
      field.filterable &&
      !RESTRICTED_FILTER_TYPES.includes(field.type) &&
      !RESTRICTED_FILTER_FIELDS.includes(field.name)
    );
  }

  private buildOptions(field: SalesforceMetadataField) {
    if (!LIST_FIELD_TYPES.includes(field.type) || !field.picklistValues) {
      return [];
    }

    return field.picklistValues?.map((option: any) => {
      return this.store.createRecord('people/attribute-option', {
        value: option.label,
        identifier: option.value,
      });
    });
  }

  private attributeIcon(type: string): InterfaceIconName {
    switch (type) {
      case 'boolean':
      case 'bool':
        return 'boolean';
      case 'number':
      case 'int':
      case 'float':
        return 'numbers';
      case 'datetime':
      case 'date':
        return 'calendar';
      case 'picklist':
      case 'multipicklist':
        return 'list';
      default:
        return 'small-text';
    }
  }

  private getAttributeType(type: string) {
    switch (type) {
      case 'boolean':
      case 'bool':
        return 'boolean';
      case 'int':
        return 'integer';
      case 'float':
        return 'float';
      case 'datetime':
      case 'date':
        return 'date';
      case 'picklist':
      case 'multipicklist':
        return 'conversation_attribute_list';
      default:
        return 'string';
    }
  }

  @action
  onUpdatePredicates(predicates: $TSFixMe) {
    this.args.onUpdatePredicates?.(predicates);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::SalesforceArticleSync::FilterPredicatesEditor': typeof FilterPredicatesEditor;
    'knowledge-hub/salesforce-article-sync/filter-predicates-editor': typeof FilterPredicatesEditor;
  }
}
